import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { adminService, OperationsHealth, RevenueSummary } from '../services/adminService';
import { paymentSettingsService, PaymentSettings } from '../services/paymentSettingsService';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const OperationsOverview: React.FC = () => {
  const [health, setHealth] = useState<OperationsHealth | null>(null);
  const [revenue, setRevenue] = useState<RevenueSummary | null>(null);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [featureSaving, setFeatureSaving] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const paymentGatewayStatuses: Record<string, { enabled: boolean; mode: string }> =
    health?.payments.gateways || {};

  const loadAll = async () => {
    try {
      const [healthData, revenueData] = await Promise.all([
        adminService.getOperationsHealth(),
        adminService.getRevenue(),
      ]);
      setHealth(healthData);
      setRevenue(revenueData);
    } catch {
      toast.error('Failed to load operations data');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      setPaymentLoading(true);
      const data = await paymentSettingsService.getSettings();
      setPaymentSettings(data);
    } catch {
      toast.error('Failed to load payment settings');
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    loadPaymentSettings();
  }, []);

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Operations Overview</Typography>

      {/* Feature Flags */}
      <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'warning.light', bgcolor: 'warning.50' }}>
        <Typography variant="h6" gutterBottom>Feature Flags</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Toggle features across the live site. Changes take effect immediately for all users.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Box flex={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentSettings?.freeRidesEnabled ?? true}
                  disabled={paymentLoading || featureSaving || !paymentSettings}
                  onChange={(e) => setPaymentSettings((prev) => prev ? { ...prev, freeRidesEnabled: e.target.checked } : prev)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    Free Rides Mode {(paymentSettings?.freeRidesEnabled ?? true) ? '— ON' : '— OFF'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ON: public /rides page shows all contacts freely. OFF: gated experience.
                  </Typography>
                </Box>
              }
            />
          </Box>
          <TextField
            label="Signup free credits"
            type="number"
            size="small"
            value={paymentSettings?.signupFreeCredits ?? 5}
            disabled={paymentLoading || featureSaving || !paymentSettings}
            onChange={(e) => {
              const parsed = Number(e.target.value);
              setPaymentSettings((prev) => prev ? {
                ...prev,
                signupFreeCredits: Number.isFinite(parsed) ? Math.max(Math.floor(parsed), 0) : 0,
              } : prev);
            }}
            inputProps={{ min: 0, step: 1 }}
            helperText="One-time wallet credits granted to each new driver at signup."
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
          />
          <Button
            variant="contained"
            color="warning"
            disabled={featureSaving || paymentLoading || !paymentSettings}
            onClick={async () => {
              if (!paymentSettings) return;
              setFeatureSaving(true);
              try {
                const updated = await paymentSettingsService.updateSettings(paymentSettings);
                setPaymentSettings(updated);
                toast.success('Platform settings saved');
              } catch {
                toast.error('Failed to save platform settings');
              } finally {
                setFeatureSaving(false);
              }
            }}
          >
            {featureSaving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary">WhatsApp Ready</Typography>
            <Typography variant="h4" fontWeight={700}>
              {health?.whatsapp.readyAccounts ?? 0}/{health?.whatsapp.activeAccounts ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {health?.whatsapp.monitoredGroupCount ?? 0} selected groups
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary">AI Queue</Typography>
            <Typography variant="h4" fontWeight={700}>{health?.aiQueue.queueSize ?? 0}</Typography>
            <Chip
              size="small"
              label={health?.aiQueue.aiEnabled ? 'AI enabled' : 'AI paused'}
              color={health?.aiQueue.aiEnabled ? 'success' : 'warning'}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary">Active Leads</Typography>
            <Typography variant="h4" fontWeight={700}>{health?.rides.activeRides ?? 0}</Typography>
            <Typography variant="body2" color="text.secondary">
              {health?.rides.ridesToday ?? 0} created today
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary">Push Coverage</Typography>
            <Typography variant="h4" fontWeight={700}>
              {health?.notifications.driversWithFcm ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              of {health?.notifications.activeDrivers ?? 0} active drivers
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        {/* System Readiness */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>System Readiness</Typography>
            <Stack spacing={1.25}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Firebase push configured</Typography>
                <Chip
                  size="small"
                  label={health?.notifications.firebaseConfigured ? 'Ready' : 'Missing'}
                  color={health?.notifications.firebaseConfigured ? 'success' : 'warning'}
                />
              </Box>
              {Object.entries(paymentGatewayStatuses).map(([gateway, status]) => (
                <Box key={gateway} display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{gateway}</Typography>
                  <Chip
                    size="small"
                    label={status.enabled ? `${status.mode} enabled` : 'Disabled'}
                    color={status.enabled ? 'success' : 'default'}
                  />
                </Box>
              ))}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Pre-filter efficiency</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {health?.aiQueue.filterStats?.filterEfficiency ?? 0}%
                </Typography>
              </Box>
            </Stack>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={loadAll}>
              Refresh
            </Button>
          </Paper>
        </Grid>

        {/* Revenue */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Revenue</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Total revenue</Typography>
                <Typography variant="h5" fontWeight={700}>{formatCurrency(revenue?.totals.totalRevenue || 0)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">This month</Typography>
                <Typography variant="h5" fontWeight={700}>{formatCurrency(revenue?.totals.monthRevenue || 0)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Active subscriptions</Typography>
                <Typography variant="h6">{revenue?.totals.activeSubscriptions ?? 0}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Unlocks this month</Typography>
                <Typography variant="h6">{revenue?.totals.unlocksThisMonth ?? 0}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Lead Quality Refund Queue */}
      <LeadRefundQueue />
    </Box>
  );
};

const LeadRefundQueue: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [refundWorkingId, setRefundWorkingId] = useState<string | null>(null);

  useEffect(() => {
    adminService.getLeadReports(false).then(setReports).catch(() => {});
  }, []);

  const refund = async (report: any) => {
    try {
      setRefundWorkingId(report._id);
      await adminService.refundLeadReport(report._id, 1);
      setReports((prev) => prev.filter((r) => r._id !== report._id));
      toast.success('Refund credit issued');
    } catch {
      toast.error('Failed to issue refund credit');
    } finally {
      setRefundWorkingId(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h6">Lead Quality Refund Queue</Typography>
          <Typography variant="body2" color="text.secondary">
            Driver reports pending review. Issuing a refund adds one bonus unlock.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => adminService.getLeadReports(false).then(setReports).catch(() => toast.error('Failed'))}
        >
          Refresh
        </Button>
      </Box>
      <Stack spacing={1.5}>
        {reports.length === 0 && (
          <Alert severity="success">No pending invalid or not-relevant lead reports.</Alert>
        )}
        {reports.map((report) => (
          <Paper key={report._id} variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="body2" fontWeight={700}>
                  {report.rideId?.aiAnalysis?.from || 'Unknown'} to {report.rideId?.aiAnalysis?.to || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {report.action === 'reported_invalid' ? 'Invalid contact' : 'Not relevant'} · {new Date(report.createdAt).toLocaleDateString('en-IN')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2">{report.userId?.name || 'Driver'}</Typography>
                <Typography variant="caption" color="text.secondary">{report.userId?.email}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {report.reason || report.metadata?.type || 'No reason provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="warning"
                  disabled={refundWorkingId === report._id}
                  onClick={() => refund(report)}
                >
                  {refundWorkingId === report._id ? 'Issuing...' : 'Refund 1'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
};

export default OperationsOverview;
